"use client";

import type { QuizData, QuizMetadata } from "./types";
import { retryableLoader } from "./retryable-loader";

type LoaderEntry = {
  slug: string;
  load: () => Promise<QuizData>;
};

type WebpackModule<T> = {
  default?: T;
} & Record<string, T | undefined>;

type WebpackRequireContext = {
  <T = unknown>(id: string): Promise<WebpackModule<T>>;
  keys(): string[];
};

declare const require: {
  context: (
    directory: string,
    useSubdirectories: boolean,
    regExp: RegExp,
    mode: "lazy"
  ) => WebpackRequireContext;
};

function normaliseSlug(filePath: string): string {
  return filePath
    .replace(/^\.\//, "")
    .replace(/^data\//, "")
    .replace(/.ts$/, "")
    .replace(/_/g, "-");
}

function extractQuizData(
  moduleExports: WebpackModule<QuizData>,
  filePath: string
): QuizData {
  if (moduleExports.default) {
    return moduleExports.default;
  }

  const firstExportedQuiz =
    Object.values(moduleExports).find(
      (value): value is QuizData =>
        typeof value === "object" && value !== undefined
    ) ?? null;

  if (!firstExportedQuiz) {
    throw new Error(
      `Quiz data module ${filePath} must export a QuizData object.`
    );
  }

  return firstExportedQuiz;
}

const context = require.context("./data", false, /.ts$/, "lazy");

const loaderEntries: LoaderEntry[] = context.keys().map((filePath) => {
  const slug = normaliseSlug(filePath);
  return {
    slug,
    load: retryableLoader(async () => {
      const moduleExports = await context<QuizData>(filePath);
      const quizData = extractQuizData(moduleExports, filePath);
      if (!quizData.meta?.title) {
        throw new Error(
          `Quiz data module ${filePath} must export a QuizData with meta.title.`
        );
      }
      return quizData;
    }),
  };
});

const loaderCache = new Map<string, LoaderEntry>();
const quizCache = new Map<string, QuizData>();
let metadataPromise: Promise<QuizMetadata[]> | null = null;

export type QuizId = string;

for (const entry of loaderEntries) {
  loaderCache.set(entry.slug, entry);
}

function resolveEntry(entry: LoaderEntry): Promise<QuizData> {
  return entry.load();
}

function registerQuiz(id: string, quiz: QuizData, entry: LoaderEntry) {
  if (quizCache.has(id) && quizCache.get(id) !== quiz) {
    throw new Error(
      `Duplicate quiz id detected: ${id} (collided at ${entry.slug}).`
    );
  }
  quizCache.set(id, quiz);
  loaderCache.set(id, entry);
}

async function resolveAndRegister(
  entry: LoaderEntry
): Promise<{ id: string; quiz: QuizData }> {
  const quiz = await resolveEntry(entry);
  const id = quiz.meta.id ?? entry.slug;
  registerQuiz(id, quiz, entry);
  return { id, quiz };
}

export async function loadQuizData(id: string): Promise<QuizData | undefined> {
  const cached = quizCache.get(id);
  if (cached) {
    return cached;
  }

  const entry = loaderCache.get(id);
  if (entry) {
    const { quiz } = await resolveAndRegister(entry);
    return quiz;
  }

  for (const candidate of loaderEntries) {
    const { id: resolvedId, quiz } = await resolveAndRegister(candidate);
    if (resolvedId === id) {
      return quiz;
    }
  }

  return undefined;
}

export async function loadQuizMetadata(): Promise<QuizMetadata[]> {
  if (!metadataPromise) {
    metadataPromise = Promise.all(
      loaderEntries.map(async (entry) => {
        const { id, quiz } = await resolveAndRegister(entry);
        return {
          id,
          title: quiz.meta.title,
          subtitle: quiz.meta.subtitle,
          adminLabel: quiz.meta.adminLabel ?? quiz.meta.title ?? id,
          track: quiz.meta.track ?? "content",
          strict2026: quiz.meta.strict2026 ?? "core",
        } satisfies QuizMetadata & { id: string };
      })
    ).then((list) =>
      list.sort((a, b) => a.adminLabel!.localeCompare(b.adminLabel!))
    ).catch((error: unknown) => {
      metadataPromise = null;
      throw error;
    });
  }

  return metadataPromise;
}
